import { useCallback, useRef } from 'react';
import {
  forceLineCoefficient,
  forceLinePrice,
  getQuote,
  getQuoteVersion,
  resetLineForcing,
  runQuoteVersionCommand,
} from '../quote.api';
import { editorFromQuote } from '../quoteEditorModel';
import { isQuoteRevision, localChangesDuringWrite } from '../quoteRevision';
import useQuoteEditorOperation from './useQuoteEditorOperation';

export default function useQuoteVersionCommands({
  editor,
  folder,
  setEditor,
  setSaving,
  setError,
  setStatusMessage,
  loadFolder,
  loadQuotes,
  reportRevisionError,
  editorOperation,
}) {
  const ownOperation = useQuoteEditorOperation({ editor, setEditor, setSaving, setError, setStatusMessage });
  const operation = editorOperation || ownOperation;
  const currentEditor = useRef(editor);
  currentEditor.current = editor;
  const forceLineValue = useCallback(async (line, kind, payload) => {
    const applyLocalForcing = (current) => ({
      ...current,
      serverCalculationStatus: 'stale',
      sections: (current.sections || []).map((section) => ({
        ...section,
        lines: (section.lines || []).map((item) => {
          if (String(item.id) !== String(line.id)) return item;
          const common = { forceReason: payload.reason, forceExpiresAt: payload.expiresAt || null, forcedAt: new Date().toISOString() };
          return kind === 'price'
            ? { ...item, ...common, sellingPriceMode: 'forced', forcedUnitSellingPrice: payload.value }
            : { ...item, ...common, coefficientMode: 'forced', forcedCoefficient: payload.value, manualCoefficient: payload.value, effectiveCoefficient: payload.value, marginCoefficient: payload.value };
        }),
      })),
    });
    if (!editor?.id || !editor.quoteVersionId || !Number.isInteger(Number(line.id))) {
      setEditor(applyLocalForcing);
      setStatusMessage(`${kind === 'price' ? 'Prix' : 'Coefficient'} forcé localement. Enregistrez le devis pour le tracer.`);
      return;
    }
    if (editor.serverCalculationStatus === 'stale' || editor.serverWriteConflict) {
      setError(editor.serverWriteConflict
        ? 'Conservez vos saisies puis rechargez explicitement la version après la commande serveur avant de forcer une valeur.'
        : 'Enregistrez les modifications avant de forcer une valeur.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const action = kind === 'price' ? forceLinePrice : forceLineCoefficient;
      const result = await action(editor.id, editor.quoteVersionId, line.id, payload, editor.revision);
      const saved = await getQuote(editor.id);
      if (currentEditor.current !== editor) throw localChangesDuringWrite();
      const nextEditor = editorFromQuote(saved);
      if (nextEditor.revision == null && isQuoteRevision(result?._revision)) nextEditor.revision = result._revision;
      setEditor(nextEditor);
      setStatusMessage(`${kind === 'price' ? 'Prix' : 'Coefficient'} forcé et tracé.`);
    } catch (error) {
      if (reportRevisionError) reportRevisionError(error);
      else setError(error.message);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [editor, setEditor, setError, setSaving, setStatusMessage, reportRevisionError]);

  const resetLineValue = useCallback(async (line, target) => {
    if (!editor?.id || !editor.quoteVersionId || !Number.isSafeInteger(Number(line.id)) || Number(line.id) <= 0
      || editor.serverCalculationStatus === 'stale' || editor.serverWriteConflict) {
      setError(editor?.serverWriteConflict
        ? 'Conservez vos saisies puis rechargez explicitement la version après la commande serveur avant de réinitialiser un forçage.'
        : 'Enregistrez les modifications avant de réinitialiser un forçage.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await resetLineForcing(editor.id, editor.quoteVersionId, line.id, target, editor.revision);
      const saved = await getQuote(editor.id);
      if (currentEditor.current !== editor) throw localChangesDuringWrite();
      const nextEditor = editorFromQuote(saved);
      if (nextEditor.revision == null && isQuoteRevision(result?._revision)) nextEditor.revision = result._revision;
      setEditor(nextEditor);
      if (folder) await loadFolder(editor.id);
      await loadQuotes();
      setStatusMessage(`${target === 'price' ? 'Prix' : 'Coefficient'} réinitialisé et devis recalculé.`);
    } catch (error) {
      if (reportRevisionError) reportRevisionError(error);
      else setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [editor, folder, setEditor, setSaving, setError, setStatusMessage, loadFolder, loadQuotes, reportRevisionError]);

  const runVersionCommand = useCallback(async (command) => {
    if (!editor?.id || !editor.quoteVersionId) return;
    const pending = operation.begin(command);
    if (!pending) return;
    try {
      const result = await runQuoteVersionCommand(editor.id, editor.quoteVersionId, command, {}, editor.revision);
      operation.succeeded(pending);
      if (!operation.selected(pending)) return;
      const saved = command === 'clone' && result?.id
        ? await getQuoteVersion(editor.id, result.id)
        : await getQuote(editor.id);
      if (!operation.selected(pending)) return;
      if (operation.apply(pending, editorFromQuote(saved), { allowVersionChange: command === 'clone', minimumRevision: result?._revision })) {
        setStatusMessage(`Commande de version « ${command} » exécutée.`);
      }
      if (folder && operation.selected(pending)) await loadFolder(editor.id);
      if (operation.selected(pending)) await loadQuotes();
    } catch (error) {
      operation.fail(pending, error, reportRevisionError);
    } finally {
      operation.finish(pending);
    }
  }, [
    editor, folder, loadFolder, loadQuotes,
    setStatusMessage, operation, reportRevisionError,
  ]);

  return { forceLineValue, resetLineValue, runVersionCommand };
}
